<template>
  <div class="space-y-4">
    <!-- Header -->
    <div class="flex justify-between items-center mb-4">
      <h2 class="text-2xl font-semibold mr-auto">{{ title }}</h2>
      <slot name="actions" />
    </div>

    <!-- Table -->
    <DataTable
      :value="items"
      :paginator="true"
      :rows="10"
      :rowsPerPageOptions="[5, 10, 25, 50]"
      :loading="loading"
      responsiveLayout="stack"
      breakpoint="960px"
      :rowHover="true"
      class="p-datatable-sm"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="currentPageReportTemplate"
      v-model:filters="filters"
      v-model:sortField="sortField"
      v-model:sortOrder="sortOrder"
      :sortMode="'single'"
      filterDisplay="row"
      filterMode="lenient"
      :globalFilterFields="globalFilterFields"
      dataKey="id"
      @row-click="onRowClick"
      ref="tableRef"
      v-model:selection="selectedItems"
      :selectOnRowClick="false"
    >
      <template #header>
        <div class="flex gap-2">
          <span class="p-input-icon-left">
            <i class="pi pi-search" />
            <InputText
              v-model="filters['global'].value"
              placeholder="Search..."
              class="p-inputtext-sm"
            />
          </span>
          <Button
            icon="pi pi-filter-slash"
            severity="secondary"
            text
            @click="clearFilters()"
            class="p-button-sm"
          />
          <Button
            v-if="exportable"
            icon="pi pi-download"
            severity="secondary"
            text
            @click="handleExport"
            class="p-button-sm"
            :disabled="items.length === 0"
          />
        </div>
      </template>

      <template #empty>
        <div class="flex flex-column align-items-center py-5">
          <i class="pi pi-inbox text-6xl text-200 mb-4"></i>
          <p class="text-500">No items found.</p>
        </div>
      </template>

      <template #loading>
        <div class="flex flex-column align-items-center py-5">
          <i class="pi pi-spin pi-spinner text-6xl text-primary mb-4"></i>
          <p>Loading items...</p>
        </div>
      </template>

      <template v-for="col in columns" :key="col.field">
        <Column
          :field="col.field"
          :header="col.header"
          :sortable="col.sortable"
          :showFilterMenu="false"
          :filter="col.filterable"
          :dataType="col.dataType"
          :style="col.style"
        >
          <template #filter="{ filterModel, filterCallback }">
            <InputText
              v-if="!col.filterOptions"
              v-model="filterModel.value"
              type="text"
              @input="filterCallback()"
              :placeholder="`Search by ${col.header.toLowerCase()}...`"
              class="p-column-filter"
            />
            <Dropdown
              v-else
              v-model="filterModel.value"
              :options="col.filterOptions"
              placeholder="Select a value"
              optionLabel="label"
              optionValue="value"
              class="p-column-filter"
              @change="filterCallback()"
            />
          </template>
          <template v-if="col.slot" #body="slotProps">
            <slot :name="col.slot" :data="slotProps.data" />
          </template>
        </Column>
      </template>

      <Column header="Actions" :exportable="false">
        <template #filter>
          <Button
            icon="pi pi-times"
            class="p-button-rounded p-button-text p-button-sm"
            @click="clearFilters()"
            aria-label="Clear filters"
          />
        </template>
        <template #body="slotProps">
          <!-- Use custom row actions slot if provided, otherwise use default actions -->
          <slot name="rowActions" :data="slotProps.data">
            <div class="flex gap-2 justify-content-center">
              <Button
                icon="pi pi-eye"
                class="p-button-rounded p-button-text"
                @click="viewItem(slotProps.data)"
                aria-label="View Item"
              />
              <Button icon="pi pi-pencil" class="p-button-text" @click="editItem(slotProps.data)" />
              <Button
                icon="pi pi-trash"
                class="p-button-text p-button-danger"
                @click="confirmDelete(slotProps.data)"
              />
            </div>
          </slot>
        </template>
      </Column>

      <Column v-if="exportable" selectionMode="multiple" headerStyle="width: 3rem"></Column>
    </DataTable>

    <!-- Last Updated Timestamp -->
    <div class="flex items-center justify-between mt-4">
      <p v-if="lastLoadedAt" class="text-sm text-gray-500">
        Last updated: {{ new Date(lastLoadedAt).toLocaleString() }}
        <a
          v-if="onReload"
          href="#"
          class="text-sm text-primary hover:underline cursor-pointer"
          :class="{ 'opacity-50 cursor-not-allowed': loading }"
          @click.prevent="onReload"
        >
          Reload
        </a>
      </p>
    </div>

    <!-- Delete Dialog -->
    <Dialog
      v-model:visible="showDeleteDialog"
      header="Confirm Delete"
      :modal="true"
      :style="{ width: '450px' }"
    >
      <div class="confirmation-content">
        <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem" />
        <span>Are you sure you want to delete {{ selectedItem?.name }}?</span>
      </div>
      <template #footer>
        <Button
          label="Cancel"
          icon="pi pi-times"
          class="p-button-text"
          @click="showDeleteDialog = false"
        />
        <Button label="Delete" icon="pi pi-check" class="p-button-danger" @click="deleteItem" />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { FilterMatchMode } from '@primevue/core/api'
import type { DataTableRowClickEvent } from 'primevue/datatable'
import type { ColumnConfig } from '@/types/ColumnConfig'

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import Dropdown from 'primevue/dropdown'

interface Props {
  title: string
  items: any[]
  columns: ColumnConfig[]
  loading: boolean
  filters: Record<string, { value: any; matchMode: string }>
  sortField?: string | number | symbol | ((item: any) => string)
  sortOrder?: number
  globalFilterFields: string[]
  currentPageReportTemplate?: string
  lastLoadedAt?: Date | null
  onReload?: () => Promise<void>
  exportable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  currentPageReportTemplate: 'Showing {first} to {last} of {totalRecords} items',
  lastLoadedAt: null,
  onReload: undefined,
  exportable: false,
})

const emit = defineEmits<{
  (e: 'view', item: any): void
  (e: 'edit', item: any): void
  (e: 'delete', item: any): void
  (e: 'update:filters', filters: Record<string, { value: any; matchMode: string }>): void
  (e: 'update:sort', field: string | null, order: number | null): void
  (e: 'update:items', items: any[]): void
  (e: 'reset:filters'): void
  (e: 'export:request', items: any[]): void
}>()

const tableRef = ref()
const showDeleteDialog = ref(false)
const selectedItem = ref<any>(null)
const selectedItems = ref<any[]>([])

// Initialize filters with default values
const filters = ref(props.filters)

// Initialize sort state
const sortField = ref<string | number | symbol | ((item: any) => string) | undefined>(
  props.sortField,
)
const sortOrder = ref<number | undefined>(props.sortOrder)

// Watch for prop changes to update local filters
watch(
  () => props.filters,
  (newFilters) => {
    filters.value = newFilters
  },
  { deep: true },
)

// Watch for sort prop changes
watch(
  () => props.sortField,
  (newField) => {
    sortField.value = newField
  },
)

watch(
  () => props.sortOrder,
  (newOrder) => {
    sortOrder.value = newOrder
  },
)

// Watch for local filter changes
watch(
  () => filters.value,
  (newFilters) => {
    emit('update:filters', newFilters)
  },
  { deep: true },
)

// Watch for local sort changes
watch(
  [sortField, sortOrder],
  ([newField, newOrder]) => {
    const field = typeof newField === 'string' ? newField : null
    emit('update:sort', field, newOrder ?? null)
  },
  { deep: true },
)

watch(
  () => tableRef.value?.processedData,
  (newData) => {
    if (newData) {
      console.log('Table processed data changed:', newData.length)
      emit('update:items', newData)
    }
  },
  { deep: true },
)

function clearFilters() {
  console.log('Clearing filters')
  emit('reset:filters')
}

function viewItem(item: any) {
  emit('view', item)
}

function editItem(item: any) {
  emit('edit', item)
}

function confirmDelete(item: any) {
  selectedItem.value = item
  showDeleteDialog.value = true
}

function deleteItem() {
  if (!selectedItem.value?.id) return
  emit('delete', selectedItem.value)
  showDeleteDialog.value = false
}

function onRowClick(event: DataTableRowClickEvent) {
  viewItem(event.data)
}

function handleExport() {
  if (selectedItems.value.length === 0) {
    // If no items selected, export all items
    emit('export:request', props.items)
  } else {
    emit('export:request', selectedItems.value)
  }
}
</script>

<style scoped>
.confirmation-content {
  display: flex;
  align-items: center;
  padding: 1rem 0;
}

:deep(.p-datatable .p-datatable-tbody > tr) {
  cursor: pointer;
}
</style>
